import React from 'react';
import clsx from 'clsx';
import CloseButton from '@components/common/CloseButton';
import styles from './GenericModal.module.css';

interface GenericModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  disableBackdropClick?: boolean;
  dataTestId?: string;
  /** Extra class on the modal content box (e.g. to override width). */
  contentClassName?: string;
  /** Extra class on the scrollable body (e.g. to control overflow). */
  bodyClassName?: string;
}

const GenericModal: React.FC<GenericModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  disableBackdropClick = false,
  dataTestId = 'modal',
  contentClassName,
  bodyClassName
}) => {
  if (!isOpen) return null;

  const handleBackdropClick = () => {
    if (!disableBackdropClick) {
      onClose();
    }
  };

  const sizeClass = {
    sm: styles.genericModalSm,
    md: styles.genericModalMd,
    lg: styles.genericModalLg
  }[size];

  return (
    <div className={styles.genericModalOverlay} data-test-id={`${dataTestId}-overlay`}>
      <button
        type="button"
        className={styles.genericModalOverlayBackdrop}
        onClick={handleBackdropClick}
        tabIndex={-1}
        aria-hidden="true"
        data-test-id={`${dataTestId}-overlay-backdrop`}
      />
      <div
        className={clsx(styles.genericModalContent, sizeClass, contentClassName)}
        data-test-id={dataTestId}
      >
        <div className={styles.genericModalHeader}>
          <h2 data-test-id={`${dataTestId}-title`}>{title}</h2>
          <CloseButton onClick={onClose} />
        </div>
        <div className={clsx(styles.genericModalBody, bodyClassName)}>{children}</div>
        {footer && <div className={styles.genericModalFooter}>{footer}</div>}
      </div>
    </div>
  );
};

export default GenericModal;
